import React, { Suspense, useEffect, useSyncExternalStore } from "react";
import ReactDOM from "react-dom/client";

import { RouterProvider } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";

import { ThemeProvider } from "styled-components";
import theme from "~/constants/theme";

import GlobalStyle from "~/globalStyle";
import { confirmReadiness } from "~/api/base";
import { maintenance, startRecovery } from "~/helpers/maintenance";
import { MaintenancePage } from "~/components/maintenance";

import "@fontsource-variable/inter";
import "@fontsource-variable/inter/wght.css";
import "@fontsource-variable/inter/wght-italic.css";
import { ToastContainer } from "~/components/toast";

import { routes } from "~/router/routes";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import "~/translations/i18n";

import { analyticsSetup, getCreateRouter } from "./helpers/analytics";
import { initDatadogRum } from "./helpers/datadog";
import { intercomKey } from "./helpers/environment";
import { ContextWrapper } from "./contexts";
import tagManagerSetup from "./helpers/tagManagerSetup";
import SuspenseFallback from "~/components/layout/SuspenseFallback";

analyticsSetup();
tagManagerSetup();

if (typeof window !== "undefined") {
  // After a deploy, a user still on the old index.html requests hashed chunks
  // that no longer exist, so a lazy route's dynamic import fails ("Failed to
  // fetch dynamically imported module" / "Importing a module script failed").
  // Vite dispatches `vite:preloadError` in that case — reload once to pick up
  // the new build, guarded so we never loop.
  window.addEventListener("vite:preloadError", () => {
    const KEY = "vite_preload_reloaded_at";
    const parsed = Number(window.sessionStorage.getItem(KEY));
    const last = Number.isFinite(parsed) ? parsed : 0;
    if (Date.now() - last > 10000) {
      window.sessionStorage.setItem(KEY, String(Date.now()));
      window.location.reload();
    }
  });
}

if (typeof window !== "undefined") {
  const runNonCritical = () => {
    initDatadogRum();
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(runNonCritical);
  } else {
    setTimeout(runNonCritical, 0);
  }
}

const createRouter = getCreateRouter();
const router = createRouter(routes);

/**
 * Outage takeover (Monday ticket 12906284771). Mounted inside `ThemeProvider`
 * (so the maintenance page gets brand tokens for its breakpoints) but
 * wrapping the router (so it can hide routed content without unmounting
 * providers). The app stays mounted and hidden rather than unmounted — that
 * preserves scroll position, open modals, and in-progress form state, so
 * recovery drops the user back exactly where they were.
 */
function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const isDown = useSyncExternalStore(
    maintenance.subscribe,
    maintenance.getSnapshot
  );

  useEffect(() => {
    if (!isDown) return;
    return startRecovery(confirmReadiness);
  }, [isDown]);

  return (
    <>
      <div hidden={isDown}>{children}</div>
      {isDown && <MaintenancePage />}
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <IntercomProvider appId={intercomKey} autoBoot={false}>
      <ContextWrapper>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
          <MaintenanceGate>
            <Suspense fallback={<SuspenseFallback />}>
              <RouterProvider router={router} />
            </Suspense>
          </MaintenanceGate>
          <ToastContainer />
          <ReactQueryDevtools initialIsOpen={false} />
        </ThemeProvider>
      </ContextWrapper>
    </IntercomProvider>
  </React.StrictMode>
);
